/**
 * The lifeline's one tooltip (Scott, 2026-10-08: "some hover over text would help with
 * navigating and understanding how the page is intended to be consumed"). Never a `title`
 * attribute: those wait for a hover delay, never show on keyboard focus, and never on touch.
 *
 *   <TipProvider> once, around the page; <Tip text="..."> around any control, HTML or SVG.
 *
 * It shows on pointer hover, on keyboard focus (focus-visible) and, on touch, on tap; it goes
 * on pointer leave, blur, Escape, a scroll, or a tap anywhere else. One bubble for the page,
 * drawn in document.body at fixed position beside its anchor (lifelineModel.placeTip: never
 * on the control, always inside the viewport), so it never takes part in any layout -- the
 * map's label placement included. The same words are the control's aria-describedby, from a
 * hidden node, so a screen reader hears them without the bubble.
 *
 * Without a provider (a static render) <Tip> renders its child untouched.
 */
import { cloneElement, createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { placeTip } from './lifelineModel'

const TipContext = createContext(null)
const HIDE_MS = 120
// placed before paint in a browser; a static render (tests) has no layout to measure
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect

export function TipProvider({ children }) {
  const [tip, setTip] = useState(null)          // { id, text, el, anchor, order }
  const [descRoot, setDescRoot] = useState(null)
  const bubble = useRef(null)
  const hideTimer = useRef(null)
  const bubbleId = `${useId()}-tip`

  const cancelHide = useCallback(() => { clearTimeout(hideTimer.current); hideTimer.current = null }, [])
  const show = useCallback((t) => { cancelHide(); setTip(t) }, [cancelHide])
  const hide = useCallback((id, now = false) => {
    cancelHide()
    const go = () => setTip(cur => (id == null || cur?.id === id ? null : cur))
    if (now) go()
    else hideTimer.current = setTimeout(go, HIDE_MS)
  }, [cancelHide])

  // dismissal: Escape, any scroll or resize, a tap/click outside the control
  useEffect(() => {
    if (!tip) return undefined
    const key = e => { if (e.key === 'Escape') hide(null, true) }
    const away = e => { if (!tip.el.contains(e.target) && !bubble.current?.contains(e.target)) hide(null, true) }
    const gone = () => hide(null, true)
    document.addEventListener('keydown', key)
    document.addEventListener('pointerdown', away, true)
    window.addEventListener('scroll', gone, true)
    window.addEventListener('resize', gone)
    return () => {
      document.removeEventListener('keydown', key)
      document.removeEventListener('pointerdown', away, true)
      window.removeEventListener('scroll', gone, true)
      window.removeEventListener('resize', gone)
    }
  }, [tip, hide])
  useEffect(() => () => clearTimeout(hideTimer.current), [])

  // beside the anchor, inside the viewport: measured, then placed, before paint
  useBeforePaint(() => {
    const b = bubble.current
    if (!tip || !b) return
    const anchor = (tip.anchor && tip.el.querySelector(tip.anchor)) || tip.el
    const r = anchor.getBoundingClientRect()
    const p = placeTip(r, b.offsetWidth, b.offsetHeight, document.documentElement.clientWidth, window.innerHeight, tip.order)
    b.style.left = `${Math.round(p.x)}px`
    b.style.top = `${Math.round(p.y)}px`
    b.dataset.side = p.side
    b.style.visibility = 'visible'
  }, [tip])

  const ctx = useMemo(() => ({ show, hide, descRoot }), [show, hide, descRoot])
  return (
    <TipContext.Provider value={ctx}>
      {children}
      <div hidden ref={setDescRoot} />
      {tip && typeof document !== 'undefined' && createPortal(
        <div ref={bubble} id={bubbleId} role="tooltip" className="ll-tip" style={{ visibility: 'hidden', left: 0, top: 0 }}
             onPointerEnter={cancelHide} onPointerLeave={() => hide(tip.id)}>
          {tip.text}
        </div>, document.body)}
    </TipContext.Provider>
  )
}

function focusVisible(el) {
  try { return el.matches(':focus-visible') } catch { return true }
}

/**
 * Wraps ONE element (a button, a span, an SVG <g>) and gives it the tooltip `text`.
 * `anchor` = a selector inside it to place against (a large region's label, say);
 * `order` = the sides to try, first that fits wins.
 */
export function Tip({ text, anchor = null, order, children }) {
  const ctx = useContext(TipContext)
  const id = useId()
  const descId = `${id}-d`
  const live = useRef(null)                    // the provider this Tip last opened in
  useEffect(() => () => live.current?.hide(id, true), [id])   // unmounted while showing: gone
  if (!ctx || !text) return children

  const open = (el) => { live.current = ctx; ctx.show({ id, text, el, anchor, order }) }
  const close = () => ctx.hide(id)
  const chain = (own, mine) => (e) => { mine(e); own?.(e) }
  const p = children.props
  return (
    <>
      {cloneElement(children, {
        'aria-describedby': [p['aria-describedby'], ctx.descRoot ? descId : null].filter(Boolean).join(' ') || undefined,
        'data-tip': text,
        onPointerEnter: chain(p.onPointerEnter, e => { if (e.pointerType !== 'touch') open(e.currentTarget) }),
        onPointerLeave: chain(p.onPointerLeave, e => { if (e.pointerType !== 'touch') close() }),
        onPointerUp: chain(p.onPointerUp, e => { if (e.pointerType === 'touch') open(e.currentTarget) }),
        onFocus: chain(p.onFocus, e => { if (e.target === e.currentTarget && focusVisible(e.currentTarget)) open(e.currentTarget) }),
        onBlur: chain(p.onBlur, () => ctx.hide(id, true)),
      })}
      {ctx.descRoot && createPortal(<span id={descId}>{text}</span>, ctx.descRoot)}
    </>
  )
}
